import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {VerifiedIcon, PackageIcon, CloudIcon} from '@primer/octicons-react'
import {artifactRegistryShowRoute} from '../routes/artifact-registry-route'
import type {Artifact} from '../types'
import {Label, LabelGroup, Link, RelativeTime} from '@primer/react'
import styles from './ArtifactListItem.module.css'
import {repositoryPath} from '@github-ui/paths'

type ArtifactListItemProps = {
  artifact: Artifact
  org: string
}

export function ArtifactListItem({artifact, org}: ArtifactListItemProps) {
  const interpunct = ' · '
  const detectionDate = new Date(Date.parse(artifact.created_at)).toLocaleDateString('default', {
    month: 'short',
    day: 'numeric',
  })
  const hasLastUpdated = artifact.updated_at && artifact.updated_at !== artifact.created_at

  const leadingIcon = artifact.attestation_id ? (
    <VerifiedIcon size={16} className={'mr-2 fgColor-success'} verticalAlign={'middle'} />
  ) : (
    <PackageIcon size={16} className={'mr-2 fgColor-muted'} verticalAlign={'middle'} />
  )

  // Build accessible title that includes deployment count
  const accessibleTitle =
    typeof artifact.deployment_count === 'number' && artifact.deployment_count > 0
      ? `${artifact.artifact_name}, ${artifact.deployment_count} ${
          artifact.deployment_count === 1 ? 'deployment' : 'deployments'
        }`
      : artifact.artifact_name

  return (
    <ListItem
      title={
        <ListItemTitle
          href={artifactRegistryShowRoute.generatePath({org, id: artifact.artifact_metadata_id.toString()})}
          value={artifact.artifact_name}
          aria-label={accessibleTitle}
        >
          {artifact.version && <span className="fgColor-muted f4 text-light ml-2">{artifact.version}</span>}
        </ListItemTitle>
      }
      metadata={
        <ListItemMetadata>
          {typeof artifact.deployment_count === 'number' && artifact.deployment_count > 0 && (
            <>
              <CloudIcon size={16} />
              <span aria-hidden="true"> {artifact.deployment_count}</span>
            </>
          )}
        </ListItemMetadata>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>{leadingIcon}</ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        {artifact.tags && artifact.tags.length > 0 && (
          <LabelGroup className="my-1">
            {artifact.tags.map(tag => (
              <Label key={`${artifact.artifact_metadata_id}-${tag}`} className={styles.TagLabel} size="small">
                {tag}
              </Label>
            ))}
          </LabelGroup>
        )}
        <ListItemDescription>
          {artifact.repository_name && (
            <ListItemDescriptionItem>
              <Link
                href={repositoryPath({owner: org, repo: artifact.repository_name})}
                data-hovercard-url={`${repositoryPath({owner: org, repo: artifact.repository_name})}/hovercard`}
                muted
              >
                {`${artifact.repository_name}`}
              </Link>
            </ListItemDescriptionItem>
          )}

          {artifact.source_registry ? (
            <ListItemDescriptionItem>
              {interpunct} Detected by {artifact.source_registry} on {detectionDate}
            </ListItemDescriptionItem>
          ) : (
            <ListItemDescriptionItem>
              {interpunct} Detected on {detectionDate}
            </ListItemDescriptionItem>
          )}

          {hasLastUpdated && (
            <ListItemDescriptionItem>
              {interpunct} Updated <RelativeTime datetime={artifact.updated_at} />
            </ListItemDescriptionItem>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

try{ ArtifactListItem.displayName ||= 'ArtifactListItem' } catch {}